import { httpClient, stringifyOpts } from 'providers/clients/httpClient';
import { fetchUtils } from 'react-admin';
import { CommonResultType, TAbortCtrlSignal } from 'utility/types/dataProvider';

export type KeywordListSearchParams = {
  query?: string;
  limit?: number;
  page?: string | number;
  [key: string]: any;
};

export type KeywordListItem = {
  id?: string | number;
  name: string;
  path: string;
};

export type GetKeywordListResult = {
  status: string;
  data: {
    total: CommonResultType['total'];
    next: CommonResultType['next'];
    prev: CommonResultType['prev'];
    items: KeywordListItem[];
    [key: string]: any;
  };
  [key: string]: any;
};

export type GetSiteTagListParams = {
  query?: string;
  limit?: number;
  page?: string;
};

export type SiteTagListItem = {
  id: number;
  path: string;
  name?: string;
  leaf?: boolean;
};

export type GetSiteTagListResult = {
  status: string | number;
  data: {
    total: CommonResultType['total'];
    next: CommonResultType['next'];
    prev: CommonResultType['prev'];
    items: SiteTagListItem[];
    [key: string]: any;
  };
  [key: string]: any;
};

export type GeoTagListSearchParams = {
  query?: string;
  limit?: number;
  page?: string | number;
  [key: string]: any;
};

export type GeoTagListItem = {
  id?: string | number;
  name: string;
  path: string;
};

export type GetGeoTagListResult = {
  status: string;
  data: {
    total: CommonResultType['total'];
    next: CommonResultType['next'];
    prev: CommonResultType['prev'];
    items: GeoTagListItem[];
    [key: string]: any;
  };
  [key: string]: any;
};

export type SectionListSearchParams = {
  query?: string;
  limit?: number;
  page?: string | number;
  signal?: TAbortCtrlSignal;
  [key: string]: any;
};

export type SectionListItem = {
  id?: string | number;
  name: string;
  path: string;
  leaf?: boolean;
};

export type GetSectionListResult = {
  status: string;
  data: {
    next?: CommonResultType['next'];
    prev?: CommonResultType['prev'];
    total?: CommonResultType['total'];
    items?: SectionListItem[];
  };
};

// Result for getSectionTree
export type SectionTreeResult = {
  status: string;
  data: {
    [key: string]: any;
    /** @description Tag's identifier */
    id?: number;
    /** @description Tag's short name */
    name?: string;
    /** @description Tag's full path (for heirarchical tag types) */
    path?: string;
    /** @description Whether or not this tag is a leaf node (for heirarchical tag types) */
    leaf?: boolean;
  }[];
  [key: string]: any;
};

export type GeoTagTreeResult = {
  status: string;
  data: {
    [key: string]: any;
    /** @description Tag's identifier */
    id?: number;
    /** @description Tag's short name */
    name?: string;
    /** @description Tag's full path (for heirarchical tag types) */
    path?: string;
    /** @description Whether or not this tag is a leaf node (for heirarchical tag types) */
    leaf?: boolean;
  }[];
  [key: string]: any;
};

type GetGeoTagTreeArgs = {
  id?: number | null | undefined;
};

type GetSectionTreeArgs = {
  id?: number | null | undefined;
};

/**
 * /core/sitetag/domains
 * Site tag names to retrieve domain mappings
 */
export type PostSiteTagDomainsParams = {
  site_tag_names?: string[];
};

/** keyed map of tag name -> domain info from a successful call */
export type SiteTagDomainMap = {
  [siteTagName: string]: { domain?: string | null | undefined };
};

export type GetSiteTagDomainsResult = {
  status?: string | number;
  data?: SiteTagDomainMap;
};

// -----------------------------------------------
// ------------ SITE TAXONOMY PROVIDER -----------
// -----------------------------------------------
export const siteTaxonomyProvider = {
  // Get keywords list
  getKeywordList: async (
    params: KeywordListSearchParams,
  ): Promise<GetKeywordListResult> => {
    try {
      const payload = {
        ...(params.query && { query: params.query }),
        ...(params.page && { page: params.page }),
        ...(params.limit && { limit: params.limit > 100 ? 99 : params.limit }),
      };

      const path = `/core/keyword/list/?${fetchUtils.queryParameters(
        payload,
        stringifyOpts,
      )}`;

      const { data, status } = await httpClient({
        url: path,
        method: 'GET',
      });

      const result: GetKeywordListResult = {
        status: data?.status || status,
        data: {
          total: data?.data?.total,
          next: data?.data?.next,
          prev: data?.data?.prev,
          items: data?.data?.items.map((item: KeywordListItem) => ({
            ...item,
          })),
        },
      };
      return result;
    } catch (error) {
      console.error('[getKeywordList] Error', error);
      throw error;
    }
  },

  // Get site section tree
  getSectionTree: async ({
    id,
  }: GetSectionTreeArgs): Promise<SectionTreeResult['data']> => {
    try {
      const path = id ? `/core/section/tree/?parent=${id}` : `/core/section/tree/`;

      const { data } = await httpClient({
        url: path,
      });

      const result: SectionTreeResult = data;
      return result?.data; // Return section tree
    } catch (error) {
      console.error('[getSectionTree] Error', error);
      throw error;
    }
  },

  // Get section list
  getSectionList: async (
    params: SectionListSearchParams,
  ): Promise<GetSectionListResult> => {
    try {
      const abortSignal = params?.signal;

      const payload = {
        ...(params.query && { query: params.query }),
        ...(params.page && { page: params.page }),
        ...(params.limit && { limit: Math.min(params.limit, 100) }),
      };

      const path = `/core/section/list/?${fetchUtils.queryParameters(
        payload,
        stringifyOpts,
      )}`;

      const { data, status } = await httpClient({
        url: path,
        method: 'GET',
        signal: abortSignal,
      });

      const result: GetSectionListResult = {
        status: data?.status || status,
        data: {
          total: data?.data?.total,
          next: data?.data?.next,
          prev: data?.data?.prev,
          items: data?.data?.items || [],
        },
      };
      return result;
    } catch (error) {
      console.error('[getSectionList]', error);
      throw error;
    }
  },

  // Get sitetag list
  getSiteTagList: async (params: GetSiteTagListParams): Promise<GetSiteTagListResult> => {
    try {
      const payload: GetSiteTagListParams = {
        ...(params?.query && { query: params.query }),
        ...(params?.page && { page: params.page }),
        ...(params?.limit && { limit: params.limit > 100 ? 99 : params.limit }),
      };

      const url = `/core/sitetag/list/?${fetchUtils.queryParameters(
        payload,
        stringifyOpts,
      )}`;

      const { data, status } = await httpClient<GetSiteTagListResult>({
        url: url,
        method: 'GET',
      });

      const result: GetSiteTagListResult = {
        status: data?.status || status,
        data: {
          total: data?.data?.total,
          next: data?.data?.next,
          prev: data?.data?.prev,
          items: data?.data?.items?.map((item: SiteTagListItem) => ({
            ...item,
          })),
        },
      };

      return result;
    } catch (error) {
      console.error('[getSiteTagList] Error', error);
      throw error;
    }
  },

  // Get site tag domains
  postSiteTagDomains: async (
    params: PostSiteTagDomainsParams,
  ): Promise<GetSiteTagDomainsResult> => {
    try {
      const { data, status } = await httpClient<GetSiteTagDomainsResult>({
        url: '/core/sitetag/domains/',
        method: 'POST',
        data: params,
      });

      const result: GetSiteTagDomainsResult = {
        status: data?.status || status,
        data: data?.data,
      };

      return result;
    } catch (error) {
      console.error('[postSiteTagDomains] Error', error);
      throw error;
    }
  },

  // Get geotag list
  getGeoTagList: async (
    params: KeywordListSearchParams,
  ): Promise<GetKeywordListResult> => {
    try {
      const payload = {
        ...(params.query && { query: params.query }),
        ...(params.page && { page: params.page }),
        ...(params.limit && { limit: params.limit > 100 ? 99 : params.limit }),
      };

      const url = `/core/geotag/list/?${fetchUtils.queryParameters(
        payload,
        stringifyOpts,
      )}`;

      const { data, status } = await httpClient({
        url: url,
        method: 'GET',
      });

      const result: GetKeywordListResult = {
        status: data?.status || status,
        data: {
          total: data.data.total,
          next: data.data.next,
          prev: data.data.prev,
          items: data.data.items.map((item: KeywordListItem) => ({
            ...item,
          })),
        },
      };
      return result;
    } catch (error) {
      console.error('[getGeoTagList] Error:', error);
      throw error;
    }
  },

  // Get geotag tree
  getGeoTagTree: async ({
    id,
  }: GetGeoTagTreeArgs): Promise<SectionTreeResult['data']> => {
    try {
      const endpoint = id ? `/core/geotag/tree/?parent=${id}` : `/core/geotag/tree/`;

      const { data } = await httpClient({
        url: endpoint,
      });

      const result: SectionTreeResult = data;
      return result.data; // Return section tree
    } catch (error) {
      console.error('[getGeoTagTree] Error:', error);
      throw error;
    }
  },
  // set as const - all keys (functions) in this object
  // are readonly and we should to ensure they
  // are immutable / cant be accidentally modified
} as const;

// Since all functions have their own signature with types
// we can safely use typeof helper to create the type for us
// and then '&' add it to the BloxCustomDataProvider type
export type SiteTaxonomyProviderType = typeof siteTaxonomyProvider;
